import { Box, Button, IconButton, Text, Tooltip, useClipboard, VStack } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column, Row, useFilters, usePagination, useSortBy, useTable } from 'react-table';
import { Shield } from '~auth/Shield';
import { CopyIcon, EditIcon } from '~components/ui/icons';
import { SubscriptionTagV2, SubscriptionTagVariant } from '~components/ui/SubscriptionTagV2';
import { generateTableCellComponent } from '~components/ui/TableCell';
import { WaveSubscriptionStatus } from '~graphql/__generated__/types';
import { EXPIRY_WARNING_DAYS } from '~utils/subscriptions';
import { SubscriptionsTableV2SubscriptionFragment } from './__generated__/SubscriptionsTable.graphql';
import { Columns } from './types';

const TableCell = generateTableCellComponent<SubscriptionsTableV2SubscriptionFragment, ReactNode>();

/** Derive the subscription tag variant based on expiry, seats, and validity */
export function getSubscriptionTagVariant(
  subscription: Pick<SubscriptionsTableV2SubscriptionFragment, 'validUntil' | 'usage' | 'status'>,
): SubscriptionTagVariant {
  const isExpired = subscription.status === WaveSubscriptionStatus.Canceled;
  const expiryDate = DateTime.fromISO(subscription.validUntil);
  const now = DateTime.now();
  const daysLeft = expiryDate.diff(now, 'days').days;

  const hasInsufficientSeats =
    subscription.usage.max !== null &&
    subscription.usage.max !== undefined &&
    subscription.usage.current > subscription.usage.max;

  if (isExpired) {
    return SubscriptionTagVariant.EssentialExpired;
  }
  if (hasInsufficientSeats) {
    return SubscriptionTagVariant.EssentialInsufficient;
  }

  if (daysLeft <= EXPIRY_WARNING_DAYS) {
    return SubscriptionTagVariant.EssentialWarning;
  }

  return SubscriptionTagVariant.Essential;
}

/** Cell component for the Subscription Name column — shows name + purchased on subtitle */
const SubscriptionNameCell = ({
  row: { original: subscription },
}: {
  row: Row<SubscriptionsTableV2SubscriptionFragment>;
}) => {
  const displayName = subscription.userGivenName ?? subscription.name;

  const purchasedDate = DateTime.fromISO(subscription.createdAt).toFormat('dd/MM/yyyy');

  return (
    <VStack alignItems="flex-start" spacing={0} overflow="hidden" width="100%">
      <Text fontWeight="bold" fontSize="sm" noOfLines={1} width="100%">
        {displayName}
      </Text>
      <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
        {t('purchasedOnDate', {
          date: purchasedDate,
          defaultValue: `Purchased on ${purchasedDate}`,
        })}
      </Text>
    </VStack>
  );
};

/** Cell component for the Subscription type badge — derives state from expiry and seats */
const SubscriptionTypeCell = ({
  row: { original: subscription },
}: {
  row: Row<SubscriptionsTableV2SubscriptionFragment>;
}) => {
  const variant = getSubscriptionTagVariant(subscription);
  return <SubscriptionTagV2 variant={variant} />;
};

/** Cell component for the Subscription ID with copy button */
const SubscriptionIdCell = ({
  row: { original: subscription },
}: {
  row: Row<SubscriptionsTableV2SubscriptionFragment>;
}) => {
  const { onCopy, hasCopied } = useClipboard(subscription.id);

  // HARDCODED: showing the first 10 characters of the ID, truncated
  const truncatedId =
    subscription.id.length > 10 ? `${subscription.id.slice(0, 10)}…` : subscription.id;

  return (
    <Box display="flex" alignItems="center" gap="1" whiteSpace="nowrap">
      <Tooltip label={subscription.id} placement="top">
        <Text fontSize="sm" color="gray.600" cursor="default" whiteSpace="nowrap">
          {truncatedId}
        </Text>
      </Tooltip>
      <Tooltip label={hasCopied ? 'Copied!' : 'Copy ID'} closeOnClick={false}>
        <IconButton
          aria-label="Copy subscription ID"
          icon={<CopyIcon />}
          size="xs"
          variant="ghost"
          onClick={onCopy}
        />
      </Tooltip>
    </Box>
  );
};

/** Cell component for the Valid Till column — shows date + months and days left */
const ValidTillCell = ({
  row: { original: subscription },
}: {
  row: Row<SubscriptionsTableV2SubscriptionFragment>;
}) => {
  const validDate = DateTime.fromISO(subscription.validUntil).toFormat('dd/MM/yy');
  const end = DateTime.fromISO(subscription.validUntil);
  const now = DateTime.now();
  const diff = end.diff(now, ['months', 'days']);
  const months = Math.floor(diff.months);
  const days = Math.floor(diff.days);

  const timeLeft =
    months > 0 ? `${months} Months left` : days > 0 ? `${days} Days left` : '0 Days left';

  return (
    <VStack alignItems="flex-start" spacing={0} overflow="hidden" width="100%">
      <Text fontSize="sm" whiteSpace="nowrap">
        {t('validTill', { defaultValue: 'Valid till' })}: {validDate}
      </Text>
      <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
        {timeLeft}
      </Text>
    </VStack>
  );
};

const SubscriptionsActionsCell = ({
  row: { original: subscription },
}: {
  row: Row<SubscriptionsTableV2SubscriptionFragment>;
}) => {
  const navigate = useNavigate();

  const handleEdit = () => navigate(subscription.id, { state: { canGoBack: true } });

  return (
    // TODO add permissions
    <Shield
      requiredPermissions={[Permission.WaveSubscriptionView, Permission.WaveSubscriptionUpdate]}
    >
      <Button size="sm" variant="inline" colorScheme="gray" onClick={handleEdit}>
        <EditIcon color="gray.300" width="actionIconSize" height="actionIconSize" />
      </Button>
    </Shield>
  );
};

export function useSubscriptionsTableV2(
  data: SubscriptionsTableV2SubscriptionFragment[],
  hideExpired = false,
) {
  const columns = useMemo<Array<Column<SubscriptionsTableV2SubscriptionFragment>>>(
    () => [
      {
        id: Columns.SubscriptionName,
        Header: t('subscriptionName', { defaultValue: 'Subscription Name' }),
        accessor: (subscription) => subscription.userGivenName ?? subscription.name,
        Cell: SubscriptionNameCell,
        sortType: (rowA, rowB) => {
          const nameA = rowA.original.userGivenName ?? rowA.original.name;
          const nameB = rowB.original.userGivenName ?? rowB.original.name;
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        },
        width: '200px',
      },
      {
        id: Columns.SubscriptionType,
        Header: t('subscription', { defaultValue: 'Subscription' }),
        accessor: (subscription) => getSubscriptionTagVariant(subscription),
        Cell: SubscriptionTypeCell,
        disableSortBy: true,
        width: '140px',
      },
      {
        id: Columns.SubscriptionId,
        Header: t('subscriptionId', { defaultValue: 'Subscription ID' }),
        accessor: (subscription) => subscription.id,
        Cell: SubscriptionIdCell,
        disableSortBy: true,
        width: '160px',
      },
      {
        id: Columns.Usage,
        Header: t('usage'),
        accessor: (subscription) =>
          subscription.usage.current !== undefined && subscription.usage.max !== undefined
            ? `${subscription.usage.current}/${subscription.usage.max}`
            : '-',
        disableSortBy: true,
        Cell: TableCell,
        width: '100px',
      },
      {
        id: Columns.ValidTill,
        Header: t('validTill', { defaultValue: 'Valid till' }),
        Cell: ValidTillCell,
        accessor: (subscription) => DateTime.fromISO(subscription.validUntil).toFormat('dd/MM/yy'),
        sortType: (rowA, rowB) =>
          DateTime.fromISO(rowA.original.validUntil).toMillis() -
          DateTime.fromISO(rowB.original.validUntil).toMillis(),
        width: '160px',
      },
      {
        id: Columns.Customers,
        Header: t('customers'),
        accessor: (subscription) =>
          subscription.customers.length === 0
            ? '-'
            : subscription.customers.map((customer) => customer.name).join(', '),
        Cell: TableCell,
        disableSortBy: true,
        width: 'auto',
        minWidth: 150,
        maxWidth: 250,
      },
      {
        id: Columns.Actions,
        Cell: SubscriptionsActionsCell,
        width: '60px',
      },
    ],
    [],
  );

  const dataMemoized = useMemo<SubscriptionsTableV2SubscriptionFragment[]>(() => {
    return hideExpired
      ? data.filter((subscription) => subscription.status === WaveSubscriptionStatus.Active)
      : data;
  }, [data, hideExpired]);

  return useTable(
    {
      columns,
      data: dataMemoized,
      initialState: {
        pageSize: 25,
        sortBy: [{ id: Columns.SubscriptionName }],
        filters: [{ id: Columns.SubscriptionName, value: [] }],
      },
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
    },
    useFilters,
    useSortBy,
    usePagination,
  );
}
